import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  RefreshCw,
  Save,
  ShieldQuestion,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";

import { ActionTracker } from "@/components/decisions/action-tracker";
import { DecisionChatPanel } from "@/components/decisions/decision-chat-panel";
import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import {
  decisions as decisionsApi,
  type ApiDecision,
  type ApiSuggestedAction,
} from "@/lib/api";

import {
  ConfidenceBadge,
  StatusBadge,
  isStale,
  relative,
} from "./$workspace.decisions";

export const Route = createFileRoute("/$workspace/decisions/$decisionId")({
  head: () => ({ meta: [{ title: "Decision — Dravian Horizon" }] }),
  component: DecisionDetailPage,
});

function DecisionDetailPage() {
  const { workspace: workspaceId, decisionId } = useParams({
    from: "/$workspace/decisions/$decisionId",
  });
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["decision", workspaceId, decisionId],
    queryFn: () => decisionsApi.get(workspaceId, decisionId),
  });
  const decision = q.data;

  // Suggested actions surface only immediately after Accept succeeds; the
  // operator confirms them and they get persisted, then the panel disappears.
  const [pendingSuggestions, setPendingSuggestions] = useState<
    ApiSuggestedAction[]
  >([]);

  return (
    <WorkspaceShell>
      <div className="p-5 max-w-[1400px] mx-auto">
        <BackLink workspaceId={workspaceId} />

        {q.isLoading ? (
          <div className="panel p-8 text-center text-[12px] text-muted-foreground">
            Loading decision…
          </div>
        ) : q.isError || !decision ? (
          <div className="panel p-8 text-center">
            <div className="text-[14px] font-semibold mb-1">
              Decision not found
            </div>
            <p className="text-[12px] text-muted-foreground">
              It may have been deleted, or you may not have access.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <div className="min-w-0 space-y-4">
              <DecisionHeader decision={decision} workspaceId={workspaceId} />
              <StalenessBanner decision={decision} />
              <AnalyzeActionBar
                decision={decision}
                workspaceId={workspaceId}
                onSuggestions={setPendingSuggestions}
                onRefetch={() => {
                  qc.invalidateQueries({
                    queryKey: ["decision", workspaceId, decisionId],
                  });
                  qc.invalidateQueries({
                    queryKey: ["decisions", workspaceId],
                  });
                }}
              />
              {decision.recommendation && (
                <>
                  <RecommendationPanel decision={decision} />
                  <EvidencePanel
                    decision={decision}
                    workspaceId={workspaceId}
                  />
                  <RisksTradeoffsPanel decision={decision} />
                  <UnknownsPanel decision={decision} />
                  <ConfidencePanel decision={decision} />
                </>
              )}
              <ActionTracker
                workspaceId={workspaceId}
                decisionId={decisionId}
                pendingSuggestions={pendingSuggestions}
                onClearSuggestions={() => setPendingSuggestions([])}
                onSuggestionsSaved={() => setPendingSuggestions([])}
              />
              <BriefingPanel decision={decision} workspaceId={workspaceId} />
            </div>

            <div className="lg:sticky lg:top-4 h-[calc(100vh-120px)]">
              <DecisionChatPanel
                workspaceId={workspaceId}
                decisionId={decisionId}
              />
            </div>
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ---------- Back link ----------

function BackLink({ workspaceId }: { workspaceId: string }) {
  return (
    <Link
      to="/$workspace/decisions"
      params={{ workspace: workspaceId }}
      className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground mb-3"
    >
      <ArrowLeft className="h-3.5 w-3.5" /> All decisions
    </Link>
  );
}

// ---------- Header + editable input ----------

function DecisionHeader({
  decision,
  workspaceId,
}: {
  decision: ApiDecision;
  workspaceId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(decision.title);
  const [input, setInput] = useState(decision.decision_input);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const patch = useMutation({
    mutationFn: () =>
      decisionsApi.patch(workspaceId, decision.id, {
        title: title.trim(),
        decision_input: input.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["decision", workspaceId, decision.id],
      });
      qc.invalidateQueries({ queryKey: ["decisions", workspaceId] });
      setEditing(false);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Failed"),
  });

  if (editing) {
    return (
      <div className="panel p-4">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full mb-3 rounded-md border border-border bg-surface px-3 py-2 text-[14px] font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
          maxLength={200}
        />
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          Decision
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={5}
          className="w-full mb-2 rounded-md border border-border bg-surface px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <p className="text-[11px] text-risk-medium mb-3">
          Editing the input will clear the current analysis. You'll need to
          re-analyze.
        </p>
        {error && (
          <div className="text-[12px] text-risk-high mb-2">{error}</div>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => {
              setTitle(decision.title);
              setInput(decision.decision_input);
              setEditing(false);
              setError(null);
            }}
            className="text-[11.5px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={() => patch.mutate()}
            disabled={patch.isPending || !title.trim() || !input.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 hover:bg-primary/90"
          >
            {patch.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            <Save className="h-3.5 w-3.5" /> Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-4">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <StatusBadge status={decision.status} />
        <ConfidenceBadge label={decision.confidence_label} />
        <span className="ml-auto text-[11px] text-muted-foreground font-mono">
          Updated {relative(new Date(decision.updated_at))}
        </span>
      </div>
      <h1 className="text-[19px] font-semibold leading-tight mb-2">
        {decision.title}
      </h1>
      <p className="text-[13px] text-foreground/85 whitespace-pre-wrap leading-relaxed">
        {decision.decision_input}
      </p>
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">
          Created {relative(new Date(decision.created_at))}
          {decision.analyzed_at && (
            <>
              {" · Analyzed "}
              {relative(new Date(decision.analyzed_at))}
            </>
          )}
        </span>
        {decision.status !== "accepted" && decision.status !== "rejected" && (
          <button
            onClick={() => setEditing(true)}
            className="text-[11.5px] text-muted-foreground hover:text-foreground"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Staleness banner ----------

function StalenessBanner({ decision }: { decision: ApiDecision }) {
  if (!isStale(decision)) return null;
  return (
    <div className="rounded-md border border-risk-medium/40 bg-risk-medium/10 px-4 py-3 flex items-start gap-2.5">
      <AlertTriangle className="h-4 w-4 text-risk-medium mt-0.5 shrink-0" />
      <div className="text-[12.5px]">
        <div className="font-semibold text-risk-medium mb-0.5">
          Analysis is outdated
        </div>
        <p className="text-muted-foreground">
          You edited the decision input after the last analysis. The
          recommendation below no longer reflects the current input — re-run
          Analyze before accepting or rejecting.
        </p>
      </div>
    </div>
  );
}

// ---------- Analyze / Accept / Reject ----------

function AnalyzeActionBar({
  decision,
  workspaceId,
  onSuggestions,
  onRefetch,
}: {
  decision: ApiDecision;
  workspaceId: string;
  onSuggestions: (s: ApiSuggestedAction[]) => void;
  onRefetch: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  const analyze = useMutation({
    mutationFn: () => decisionsApi.analyze(workspaceId, decision.id),
    onSuccess: () => {
      setError(null);
      onRefetch();
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.toLowerCase().includes("timed out") || msg.includes("504")) {
        setError(
          "The AI took longer than 45 seconds. This usually happens on very large workspaces or a slow LLM provider. Retry, or edit the decision to be more specific.",
        );
      } else if (msg.includes("502")) {
        setError(
          "The AI returned a response the backend couldn't parse. Retry — this is usually transient.",
        );
      } else {
        setError(msg);
      }
    },
  });

  const accept = useMutation({
    mutationFn: () => decisionsApi.accept(workspaceId, decision.id),
    onSuccess: (res) => {
      onSuggestions(res.suggested_actions);
      setSuggestionError(res.suggestion_error);
      setError(null);
      onRefetch();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const reject = useMutation({
    mutationFn: (reason: string | undefined) =>
      decisionsApi.reject(workspaceId, decision.id, reason),
    onSuccess: () => {
      setError(null);
      onRefetch();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const [rejectReason, setRejectReason] = useState("");
  const [askingRejectReason, setAskingRejectReason] = useState(false);
  const [confirmingAccept, setConfirmingAccept] = useState(false);

  const canDecide =
    decision.status === "analyzed" && !isStale(decision) && !analyze.isPending;
  const decided =
    decision.status === "accepted" || decision.status === "rejected";

  return (
    <div className="panel p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => analyze.mutate()}
          disabled={analyze.isPending || decided}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
          title={
            decided ? "This decision has already been closed" : undefined
          }
        >
          {analyze.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Analyzing (up to 45s)…
            </>
          ) : decision.status === "draft" && !decision.analyzed_at ? (
            <>
              <Sparkles className="h-3.5 w-3.5" /> Analyze
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5" /> Re-analyze
            </>
          )}
        </button>

        <div className="mx-2 h-5 w-px bg-border" />

        {!confirmingAccept ? (
          <button
            onClick={() => setConfirmingAccept(true)}
            disabled={!canDecide || accept.isPending || reject.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-risk-low text-white px-4 py-2 text-[13px] font-semibold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-risk-low/90"
            title={
              isStale(decision)
                ? "Re-analyze before accepting — the input changed"
                : decision.status === "draft"
                  ? "Analyze first"
                  : "Accept this decision — records who decided and drafts follow-up actions"
            }
          >
            <CheckCircle2 className="h-4 w-4" />
            Accept decision
          </button>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-md border border-risk-low/60 bg-risk-low/10 px-2.5 py-1.5">
            <span className="text-[11.5px] font-semibold text-risk-low">
              Confirm accept?
            </span>
            <button
              onClick={() => {
                accept.mutate();
                setConfirmingAccept(false);
              }}
              disabled={accept.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-risk-low text-white px-3 py-1 text-[12px] font-semibold hover:bg-risk-low/90 disabled:opacity-50"
            >
              {accept.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Yes, accept
            </button>
            <button
              onClick={() => setConfirmingAccept(false)}
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        )}

        {!askingRejectReason ? (
          <button
            onClick={() => setAskingRejectReason(true)}
            disabled={!canDecide || accept.isPending || reject.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-risk-high/40 bg-risk-high/10 text-risk-high px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-risk-high/20"
          >
            <XCircle className="h-3.5 w-3.5" /> Reject
          </button>
        ) : (
          <div className="inline-flex items-center gap-2">
            <input
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (optional)"
              className="rounded-md border border-border bg-surface px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={() => {
                reject.mutate(rejectReason.trim() || undefined);
                setAskingRejectReason(false);
                setRejectReason("");
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-risk-high text-white px-3 py-1.5 text-[12.5px] font-semibold hover:bg-risk-high/90"
            >
              {reject.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Confirm reject
            </button>
            <button
              onClick={() => {
                setAskingRejectReason(false);
                setRejectReason("");
              }}
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        )}

        {decision.status === "draft" && !decision.analyzed_at && (
          <span className="text-[11px] text-muted-foreground ml-auto">
            Run Analyze to see a recommendation
          </span>
        )}
        {decision.status === "accepted" && (
          <span className="text-[11px] text-risk-low ml-auto font-semibold">
            Accepted
          </span>
        )}
        {decision.status === "rejected" && (
          <span className="text-[11px] text-risk-high ml-auto font-semibold">
            Rejected
          </span>
        )}
      </div>

      {/* Explicit error state — never a generic toast */}
      {error && (
        <div className="mt-3 rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
          <div className="font-semibold mb-0.5">Something went wrong</div>
          <div className="text-muted-foreground whitespace-pre-wrap">
            {error}
          </div>
        </div>
      )}
      {suggestionError && (
        <div className="mt-3 rounded-md border border-risk-medium/40 bg-risk-medium/10 px-3 py-2 text-[12px] text-risk-medium">
          <div className="font-semibold mb-0.5">
            Accepted — but couldn't suggest actions
          </div>
          <div className="text-muted-foreground">{suggestionError}</div>
        </div>
      )}
    </div>
  );
}

// ---------- Recommendation panel ----------

function RecommendationPanel({ decision }: { decision: ApiDecision }) {
  const rec = decision.recommendation!;
  const styles: Record<typeof rec, { color: string; icon: React.ReactNode; label: string }> = {
    accept: {
      color: "text-risk-low border-risk-low/40 bg-risk-low/10",
      icon: <CheckCircle2 className="h-4 w-4" />,
      label: "Accept",
    },
    reject: {
      color: "text-risk-high border-risk-high/40 bg-risk-high/10",
      icon: <XCircle className="h-4 w-4" />,
      label: "Reject",
    },
    revise: {
      color: "text-risk-medium border-risk-medium/40 bg-risk-medium/10",
      icon: <ChevronUp className="h-4 w-4" />,
      label: "Revise",
    },
    insufficient_data: {
      color: "text-muted-foreground border-border bg-muted/20",
      icon: <ShieldQuestion className="h-4 w-4" />,
      label: "Insufficient data",
    },
  };
  const s = styles[rec];

  return (
    <div className="panel p-4">
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${s.color}`}
        >
          {s.icon} Recommendation: {s.label}
        </div>
      </div>
      {decision.summary && (
        <p className="text-[13.5px] font-semibold leading-relaxed">
          {decision.summary}
        </p>
      )}
      {decision.rationale && (
        <p className="text-[12.5px] text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">
          {decision.rationale}
        </p>
      )}
    </div>
  );
}

// ---------- Evidence ----------

function EvidencePanel({
  decision,
  workspaceId,
}: {
  decision: ApiDecision;
  workspaceId: string;
}) {
  const { entity_ids, signal_ids, scenario_ids } = decision.evidence;
  const total = entity_ids.length + signal_ids.length + scenario_ids.length;
  if (total === 0) return null;
  return (
    <div className="panel p-4">
      <div className="label-eyebrow mb-1.5">Evidence</div>
      <div className="text-[13px] font-semibold mb-2">
        {total} reference{total === 1 ? "" : "s"} from this workspace
      </div>
      <div className="grid sm:grid-cols-3 gap-3 text-[12px]">
        <EvidenceGroup
          title="Entities"
          ids={entity_ids}
          workspaceId={workspaceId}
          kind="entity"
        />
        <EvidenceGroup
          title="Signals"
          ids={signal_ids}
          workspaceId={workspaceId}
          kind="signal"
        />
        <EvidenceGroup
          title="Scenarios"
          ids={scenario_ids}
          workspaceId={workspaceId}
          kind="scenario"
        />
      </div>
      <p className="text-[10.5px] text-muted-foreground mt-3">
        Click an entity to open it in the workspace inspector. IDs shown are
        truncated; hover for the full UUID.
      </p>
    </div>
  );
}

function EvidenceGroup({
  title,
  ids,
  workspaceId,
  kind,
}: {
  title: string;
  ids: string[];
  workspaceId: string;
  kind: "entity" | "signal" | "scenario";
}) {
  return (
    <div>
      <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
        {title} · {ids.length}
      </div>
      {ids.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">None</div>
      ) : (
        <div className="space-y-1">
          {ids.slice(0, 6).map((id) => (
            <EvidenceLink
              key={id}
              id={id}
              workspaceId={workspaceId}
              kind={kind}
            />
          ))}
          {ids.length > 6 && (
            <div className="text-[10.5px] text-muted-foreground italic">
              +{ids.length - 6} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceLink({
  id,
  workspaceId,
  kind,
}: {
  id: string;
  workspaceId: string;
  kind: "entity" | "signal" | "scenario";
}) {
  const navigate = useNavigate();
  const label = (
    <span className="font-mono text-[10.5px] truncate" title={id}>
      {id.slice(0, 8)}…
    </span>
  );
  if (kind === "entity") {
    // The workspace layout listens to ?entity=<id> and pops the slide-over
    // inspector. Same pattern used from Network / Timeline canvases — the
    // slide-over overlays the current page, so the operator stays on the
    // decision detail while inspecting the cited entity.
    return (
      <button
        onClick={() =>
          navigate({
            to: ".",
            search: (prev) => ({ ...prev, entity: id }),
          })
        }
        className="block w-full text-left text-muted-foreground hover:text-primary transition"
      >
        {label}
      </button>
    );
  }
  if (kind === "scenario") {
    return (
      <Link
        to="/$workspace/scenario-tree"
        params={{ workspace: workspaceId }}
        className="block text-muted-foreground hover:text-primary transition"
      >
        {label}
      </Link>
    );
  }
  // Signals don't have their own slide-over yet — fall back to the list view.
  return (
    <Link
      to="/$workspace/signals"
      params={{ workspace: workspaceId }}
      className="block text-muted-foreground hover:text-primary transition"
    >
      {label}
    </Link>
  );
}

// ---------- Risks + Tradeoffs ----------

function RisksTradeoffsPanel({ decision }: { decision: ApiDecision }) {
  if (decision.risks.length === 0 && decision.tradeoffs.length === 0) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {decision.risks.length > 0 && (
        <div className="panel p-4">
          <div className="label-eyebrow mb-2">Risks</div>
          <ul className="space-y-2">
            {decision.risks.map((r, i) => (
              <li key={i} className="text-[12.5px]">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider ${
                      r.severity === "high"
                        ? "bg-risk-high/15 text-risk-high border border-risk-high/30"
                        : r.severity === "medium"
                          ? "bg-risk-medium/15 text-risk-medium border border-risk-medium/30"
                          : "bg-risk-low/15 text-risk-low border border-risk-low/30"
                    }`}
                  >
                    {r.severity}
                  </span>
                  <span className="font-semibold">{r.label}</span>
                </div>
                {r.detail && (
                  <p className="text-muted-foreground mt-0.5 leading-relaxed">
                    {r.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {decision.tradeoffs.length > 0 && (
        <div className="panel p-4">
          <div className="label-eyebrow mb-2">Tradeoffs</div>
          <ul className="space-y-2">
            {decision.tradeoffs.map((t, i) => (
              <li key={i} className="text-[12.5px]">
                <div className="font-semibold">{t.label}</div>
                {t.detail && (
                  <p className="text-muted-foreground mt-0.5 leading-relaxed">
                    {t.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- Unknowns ----------

function UnknownsPanel({ decision }: { decision: ApiDecision }) {
  if (decision.unknowns.length === 0) return null;
  return (
    <div className="panel p-4">
      <div className="label-eyebrow mb-2">What we don't know</div>
      <ul className="space-y-1.5">
        {decision.unknowns.map((u, i) => (
          <li
            key={i}
            className="text-[12.5px] text-muted-foreground flex items-start gap-1.5"
          >
            <span className="text-primary/60 mt-1">·</span>
            <span>{u}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Confidence panel ----------

function ConfidencePanel({ decision }: { decision: ApiDecision }) {
  const [expanded, setExpanded] = useState(false);
  const r = decision.confidence_reasons;
  const hasContent =
    r.evidence_count > 0 ||
    r.contradictions.length > 0 ||
    r.gaps.length > 0 ||
    r.insufficient_grounding;
  const summary = useMemo(() => {
    const bits: string[] = [];
    bits.push(`${r.evidence_count} piece${r.evidence_count === 1 ? "" : "s"} of evidence`);
    if (r.recency_days !== null) {
      bits.push(`newest ${r.recency_days}d old`);
    }
    if (r.source_diversity > 0) {
      bits.push(`${r.source_diversity} distinct source type${r.source_diversity === 1 ? "" : "s"}`);
    }
    return bits.join(" · ");
  }, [r]);

  return (
    <div className="panel p-4">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <div className="label-eyebrow mb-0.5">Confidence</div>
          <div className="flex items-center gap-2">
            <ConfidenceBadge label={decision.confidence_label} />
            {hasContent && (
              <span className="text-[11.5px] text-muted-foreground">
                {summary}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border text-[12px] space-y-2.5">
          {r.insufficient_grounding && (
            <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-risk-high">
              <div className="font-semibold mb-0.5">Insufficient grounding</div>
              <div className="text-muted-foreground">
                The AI cited entities or signals that don't exist in this
                workspace. Confidence has been forced to low.
              </div>
            </div>
          )}
          <ConfidenceMetric
            label="Evidence"
            value={String(r.evidence_count)}
            hint="entities + signals + scenarios cited"
          />
          {r.recency_days !== null && (
            <ConfidenceMetric
              label="Recency"
              value={`${r.recency_days} days`}
              hint="age of the newest cited signal"
            />
          )}
          <ConfidenceMetric
            label="Source diversity"
            value={String(r.source_diversity)}
            hint="distinct signal types among cited evidence"
          />
          {r.contradictions.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-risk-high mb-1">
                Contradictions
              </div>
              <ul className="space-y-1 text-[12px] text-muted-foreground">
                {r.contradictions.map((c, i) => (
                  <li key={i}>· {c}</li>
                ))}
              </ul>
            </div>
          )}
          {r.gaps.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Information gaps
              </div>
              <ul className="space-y-1 text-[12px] text-muted-foreground">
                {r.gaps.map((g, i) => (
                  <li key={i}>· {g}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfidenceMetric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground w-32 shrink-0">
        {label}
      </span>
      <span className="text-[13px] font-semibold">{value}</span>
      <span className="text-[11px] text-muted-foreground">— {hint}</span>
    </div>
  );
}

// ---------- Briefing (drawer-lite) ----------

function BriefingPanel({
  decision,
  workspaceId,
}: {
  decision: ApiDecision;
  workspaceId: string;
}) {
  const [open, setOpen] = useState(false);
  const [markdown, setMarkdown] = useState(decision.briefing_markdown);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const gen = useMutation({
    mutationFn: () => decisionsApi.briefing(workspaceId, decision.id),
    onSuccess: (res) => {
      setMarkdown(res.markdown);
      setError(null);
      qc.invalidateQueries({
        queryKey: ["decision", workspaceId, decision.id],
      });
      setOpen(true);
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.toLowerCase().includes("timed out") || msg.includes("504")) {
        setError(
          "The briefing took longer than 45 seconds. Retry, or narrow the decision.",
        );
      } else {
        setError(msg);
      }
    },
  });

  const copy = async () => {
    if (!markdown) return;
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!decision.recommendation) return null;
  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow mb-0.5">Briefing</div>
          <div className="text-[13px] font-semibold">
            {markdown
              ? `Generated ${decision.briefing_generated_at ? relative(new Date(decision.briefing_generated_at)) : "recently"}`
              : "No briefing yet"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {markdown && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              {open ? "Hide" : "Show"}
            </button>
          )}
          <button
            onClick={() => gen.mutate()}
            disabled={gen.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11.5px] font-semibold hover:bg-surface-elevated disabled:opacity-50"
          >
            {gen.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            {markdown ? "Regenerate" : "Generate briefing"}
          </button>
        </div>
      </div>
      {error && (
        <div className="mt-3 rounded-md border border-risk-medium/40 bg-risk-medium/10 px-3 py-2 text-[12px] text-risk-medium">
          {error}
        </div>
      )}
      {open && markdown && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-end gap-2 mb-2">
            <button
              onClick={copy}
              className="text-[11.5px] text-muted-foreground hover:text-foreground"
            >
              {copied ? "Copied ✓" : "Copy markdown"}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-[12.5px] font-sans leading-relaxed text-foreground/90 max-h-[500px] overflow-y-auto scrollbar-thin border border-border rounded-md p-3 bg-surface">
            {markdown}
          </pre>
        </div>
      )}
    </div>
  );
}
